import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Clock,
  ListChecks,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Copy,
  FileText,
  Loader2,
  RefreshCw
} from 'lucide-react';
const STEP_TYPES = [
  { value: 'discussion', label: 'Discussion', color: 'bg-blue-100 text-blue-700' },
  { value: 'exercise', label: 'Exercise', color: 'bg-green-100 text-green-700' },
  { value: 'reflection', label: 'Reflection', color: 'bg-purple-100 text-purple-700' },
  { value: 'break', label: 'Break', color: 'bg-gray-100 text-gray-700' },
  { value: 'assessment', label: 'Assessment', color: 'bg-amber-100 text-amber-700' }
];

const TEMPLATE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'edge', label: 'EDGE Diagnostic' },
  { value: 'core', label: 'CORE Diagnostic' },
  { value: 'combined', label: 'Combined Session' }
];

export default function AdminSessionTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'general',
    steps: [],
    estimatedDuration: 60,
    isActive: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/session-templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Failed to load session templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      type: 'general',
      steps: [
        { title: 'Welcome & Introduction', description: 'Set context and establish rapport', duration: 5, type: 'discussion' },
        { title: 'Main Session', description: 'Core session content', duration: 40, type: 'assessment' },
        { title: 'Reflection & Next Steps', description: 'Discuss insights and action items', duration: 10, type: 'reflection' },
        { title: 'Close', description: 'Wrap up and schedule follow-up', duration: 5, type: 'discussion' }
      ],
      estimatedDuration: 60,
      isActive: true
    });
    setShowEditor(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      type: template.type || 'general',
      steps: template.steps || [],
      estimatedDuration: template.estimatedDuration || 60,
      isActive: template.isActive !== false
    });
    setShowEditor(true);
  };

  const handleDuplicate = async (template) => {
    try {
      const response = await axios.post('/api/admin/session-templates', {
        name: `${template.name} (Copy)`,
        description: template.description,
        type: template.type,
        steps: template.steps,
        estimatedDuration: template.estimatedDuration,
        isActive: false
      });
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`/api/admin/session-templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`/api/admin/session-templates/${editingTemplate.id}`, formData);
        toast.success('Template updated');
      } else {
        await axios.post('/api/admin/session-templates', formData);
        toast.success('Template created');
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Step management functions
  const addStep = () => {
    setFormData({
      ...formData,
      steps: [...formData.steps, { title: '', description: '', duration: 10, type: 'discussion' }]
    });
  };

  const removeStep = (index) => {
    setFormData({
      ...formData,
      steps: formData.steps.filter((_, i) => i !== index)
    });
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setFormData({ ...formData, steps: newSteps });
  };

  const moveStep = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.steps.length) return;
    
    const newSteps = [...formData.steps];
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    setFormData({ ...formData, steps: newSteps });
  };

  // Calculate total duration from steps
  const totalStepsDuration = formData.steps.reduce((sum, step) => sum + (step.duration || 0), 0);

  return (
      <div data-testid="admin-session-templates">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <ListChecks className="w-6 h-6 text-primary" />
              Session Templates
            </h1>
            <p className="text-muted-foreground">
              Create and manage structured session agendas for facilitators
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchTemplates} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleCreate} data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" /> New Template
            </Button>
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ListChecks className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No session templates yet</p>
              <Button onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" /> Create First Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <Card key={template.id} className={!template.isActive ? 'opacity-60' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {template.name}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {template.description || 'No description'}
                      </CardDescription>
                    </div>
                    {!template.isActive && (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-sm">
                      <Badge variant="outline">
                        {TEMPLATE_TYPES.find(t => t.value === template.type)?.label || template.type}
                      </Badge>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {template.estimatedDuration} min
                      </span>
                      <span className="text-muted-foreground">
                        {template.steps?.length || 0} steps
                      </span>
                    </div>
                    
                    {/* Steps Preview */}
                    <div className="space-y-1">
                      {template.steps?.slice(0, 3).map((step, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                          <span className="w-4 h-4 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                            {idx + 1}
                          </span>
                          <span className="truncate flex-1">{step.title}</span>
                          <span className="text-muted-foreground">{step.duration}m</span>
                        </div>
                      ))}
                      {template.steps?.length > 3 && (
                        <p className="text-xs text-muted-foreground pl-6">
                          +{template.steps.length - 3} more steps
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(template)}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDuplicate(template)}>
                        <Copy className="w-3 h-3 mr-1" /> Duplicate
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(template.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Editor Dialog */}
        <Dialog open={showEditor} onOpenChange={setShowEditor}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Session Template' : 'Create Session Template'}
              </DialogTitle>
              <DialogDescription>
                Define the structure and flow of facilitated sessions
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Template Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Standard EDGE Session"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Session Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of when to use this template"
                  rows={2}
                />
              </div>

              {/* Steps */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Session Steps</Label>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">
                      Total: <strong>{totalStepsDuration} min</strong>
                    </span>
                    <Button size="sm" variant="outline" onClick={addStep}>
                      <Plus className="w-3 h-3 mr-1" /> Add Step
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  {formData.steps.map((step, index) => (
                    <div 
                      key={index} 
                      className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg border"
                    >
                      <div className="flex flex-col gap-1 pt-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => moveStep(index, 'up')}
                          disabled={index === 0}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => moveStep(index, 'down')}
                          disabled={index === formData.steps.length - 1}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="flex-1 grid gap-2 md:grid-cols-4">
                        <div className="md:col-span-2">
                          <Input
                            placeholder="Step title"
                            value={step.title}
                            onChange={(e) => updateStep(index, 'title', e.target.value)}
                          />
                        </div>
                        <Select
                          value={step.type}
                          onValueChange={(value) => updateStep(index, 'type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STEP_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={step.duration}
                            onChange={(e) => updateStep(index, 'duration', parseInt(e.target.value) || 0)}
                            className="w-20"
                          />
                          <span className="text-sm text-muted-foreground">min</span>
                        </div>
                        <div className="md:col-span-4">
                          <Input
                            placeholder="Description (optional)"
                            value={step.description}
                            onChange={(e) => updateStep(index, 'description', e.target.value)}
                          />
                        </div>
                      </div>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeStep(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Settings */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div>
                  <Label>Active Template</Label>
                  <p className="text-sm text-muted-foreground">
                    Inactive templates are hidden from facilitators
                  </p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditor(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingTemplate ? 'Update Template' : 'Create Template'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
}
